import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ScrollArea } from "../../ui/scroll-area";
import { Checkbox } from "../../ui/checkbox";
import { Users, Loader2 } from "lucide-react";
import { listStudents } from "../../../lib/api";

export function StudentSelector({ targetType, selectedStudentIds, onTargetChange, onStudentsChange }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const { data } = await listStudents();
      setStudents(data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudent = (studentId) => {
    if (selectedStudentIds.includes(studentId)) {
      onStudentsChange(selectedStudentIds.filter((id) => id !== studentId));
    } else {
      onStudentsChange([...selectedStudentIds, studentId]);
    }
  };

  const selectAll = () => {
    onStudentsChange(students.map((s) => s.id));
  };

  const selectNone = () => {
    onStudentsChange([]);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button
          variant={targetType === "whole_class" ? "default" : "outline"}
          size="sm"
          onClick={() => onTargetChange("whole_class")}
          className="flex-1"
        >
          <Users className="w-4 h-4 mr-1" />
          Classe entiere
        </Button>
        <Button
          variant={targetType === "group" ? "default" : "outline"}
          size="sm"
          onClick={() => onTargetChange("group")}
          className="flex-1"
        >
          <Users className="w-4 h-4 mr-1" />
          Groupe
        </Button>
        <Button
          variant={targetType === "individual" ? "default" : "outline"}
          size="sm"
          onClick={() => onTargetChange("individual")}
          className="flex-1"
        >
          <Users className="w-4 h-4 mr-1" />
          Individuel
        </Button>
      </div>

      {targetType !== "whole_class" && (
        <>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">
              {selectedStudentIds.length} élève(s) sélectionné(s)
            </span>
            <div className="flex gap-2">
              <button className="text-indigo-600 hover:underline" onClick={selectAll}>
                Tous
              </button>
              <button className="text-slate-500 hover:underline" onClick={selectNone}>
                Aucun
              </button>
            </div>
          </div>

          <ScrollArea className="h-[180px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : students.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 p-4">
                <Users className="w-8 h-8 mb-2" />
                <p>Aucun élève</p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {students.map((student) => (
                  <div
                    key={student.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      selectedStudentIds.includes(student.id)
                        ? "bg-indigo-50 border border-indigo-200"
                        : "hover:bg-slate-50"
                    }`}
                    onClick={() => toggleStudent(student.id)}
                  >
                    <Checkbox checked={selectedStudentIds.includes(student.id)} />
                    <span className="text-sm">{student.student_name}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      {student.niveau?.toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </>
      )}
    </div>
  );
}

export default StudentSelector;
